<template>
  <v-data-table
    :headers="bomDataTableHeaders"
    :items="bomList"
    :search="searchBom"
    class="elevation-1 ma-5"
    show-expand
    single-expand
    item-key="Id"
    :no-data-text="$t('common_no_data')"
    :no-results-text="$t('common_no_results')"
    :loading="isLoadingBom"
    :loading-text="$t('common_get_data')"
    :footer-props="{
      'items-per-page-options': [30, 50, 100],
      'items-per-page-text': $t('item_rows_per_page'),
    }"
    :header-props="headerProps"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title>{{ $t('bom_tool_bar_title') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="searchBom"
          append-icon="mdi-magnify"
          :label="$t('bom_search_label')"
          single-line
          hide-details
          clearable
        ></v-text-field>
        <v-dialog v-model="isShowEditBomDialog" fullscreen persistent>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" dark class="mb-2 ml-8" v-bind="attrs" v-on="on">
              {{ $t('bom_new_btn') }}
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ bomDialogTitle }}</span>
              <v-spacer></v-spacer>
              <v-btn v-if="editedBomIndex !== -1" icon fab @click="copyBom">
                <v-icon>mdi-content-copy</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-container>
                <ValidationObserver ref="dialogNameAndPrice">
                  <v-row>
                    <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                      <v-checkbox
                        v-model="editedBom.IsActive"
                        :label="$t('common_is_active')"
                        color="primary"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                      <ValidationProvider
                        :name="$t('bom_name')"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="editedBom.Name"
                          :error-messages="errors[0]"
                          required
                          clearable
                          :disabled="isAllTextFieldDisable || editedBomIndex !== -1"
                          @keyup.enter="focusNextInput"
                        >
                          <template v-slot:label>
                            <span class="red--text">
                              <strong>* </strong>
                            </span>
                            {{ $t('bom_name') }}
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                      <ValidationProvider
                        :name="$t('bom_quantity')"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="editedBom.Quantity"
                          :error-messages="errors[0]"
                          required
                          clearable
                          :disabled="isAllTextFieldDisable"
                          @keyup.enter="focusNextInput"
                        >
                          <template v-slot:label>
                            <span class="red--text">
                              <strong>* </strong>
                            </span>
                            {{ $t('bom_quantity') }}
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                      <ValidationProvider
                        :name="$t('bom_price')"
                        :rules="{ required: true, regex: /^[0-9]+$/ }"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="editedBom.Price"
                          :error-messages="errors[0]"
                          required
                          clearable
                          :disabled="isAllTextFieldDisable"
                          @keyup.enter="focusNextInput"
                        >
                          <template v-slot:label>
                            <span class="red--text">
                              <strong>* </strong>
                            </span>
                            {{ $t('bom_price') }}
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                      <ValidationProvider
                        :name="$t('bom_unit')"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-text-field
                          v-model="editedBom.Unit"
                          :error-messages="errors[0]"
                          required
                          clearable
                          :disabled="isAllTextFieldDisable"
                          @keyup.enter="focusNextInput"
                        >
                          <template v-slot:label>
                            <span class="red--text">
                              <strong>* </strong>
                            </span>
                            {{ $t('bom_unit') }}
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        outlined
                        v-model="editedBom.Describe"
                        :label="$t('bom_describe')"
                        :disabled="isAllTextFieldDisable"
                        @keyup.enter="focusNextInput"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        outlined
                        v-model="editedBom.Remark"
                        :label="$t('quotation_item_remark')"
                        :disabled="isAllTextFieldDisable"
                        @keyup.enter="focusNextInput"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </ValidationObserver>
              </v-container>
            </v-card-text>

            <v-data-table
              :headers="bomItemDataTableHeaders"
              :items="editedBom.BomItems"
              :search="searchBomItem"
              class="elevation-1  mt-5 mx-10"
              hide-default-footer
              disable-pagination
              :no-data-text="$t('common_no_data')"
              :no-results-text="$t('common_no_results')"
              :header-props="headerProps"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>{{ $t('bom_item_tool_bar_title') }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchBomItem"
                    append-icon="mdi-magnify"
                    :label="$t('bom_item_search_label')"
                    single-line
                    hide-details
                    clearable
                  ></v-text-field>
                  <v-dialog v-model="isShowEditBomItemDialog" persistent>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        dark
                        class="mb-2 ml-8"
                        v-bind="attrs"
                        v-on="on"
                        :disabled="isAllTextFieldDisable"
                      >
                        {{ $t('bom_item_new_btn') }}
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title>
                        <span class="text-h5">{{ bomItemDialogTitle }}</span>
                      </v-card-title>
                      <v-card-text>
                        <v-container>
                          <ValidationObserver ref="dialogBomItem">
                            <v-row>
                              <v-col cols="12" sm="6" md="4" xl="4" lg="4">
                                <item-filter-all-type-autocomplete
                                  refName="itemAutocomplete"
                                  :fatherEditedItem="editedItem"
                                  :editedItemIndex="editedBomItemIndex"
                                  :itemName="'bom_item_number'"
                                  @setEditedItem="setEditedItem"
                                  @keyupEnter="focusNextInput"
                                  v-if="editedBomItemIndex === -1 && isShowEditBomItemDialog"
                                />
                                <ValidationProvider
                                  :name="$t('bom_item_number')"
                                  rules="required"
                                  v-slot="{ errors }"
                                  v-else
                                >
                                  <v-text-field
                                    v-model="editedItem.Number"
                                    disabled
                                    required
                                    :error-messages="errors[0]"
                                  >
                                    <template v-slot:label>
                                      <span class="red--text">
                                        <strong>* </strong>
                                      </span>
                                      {{ $t('bom_item_number') }}
                                    </template>
                                  </v-text-field>
                                </ValidationProvider>
                              </v-col>
                              <v-col cols="12" sm="6" md="4" xl="4" lg="4">
                                <ValidationProvider
                                  :name="$t('bom_item_name')"
                                  rules="required"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    v-model="editedItem.Name"
                                    disabled
                                    :error-messages="errors[0]"
                                  >
                                    <template v-slot:label>
                                      <span class="red--text">
                                        <strong>* </strong>
                                      </span>
                                      {{ $t('bom_item_name') }}
                                    </template>
                                  </v-text-field>
                                </ValidationProvider>
                              </v-col>
                              <v-col cols="12" sm="6" md="4" xl="4" lg="4">
                                <ValidationProvider
                                  :name="$t('bom_item_quantity')"
                                  :rules="{ required: true, regex: /^[0-9]+$/ }"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    v-model="editedItem.Quantity"
                                    required
                                    :error-messages="errors[0]"
                                    clearable
                                    @keyup.enter="focusNextInput"
                                  >
                                    <template v-slot:label>
                                      <span class="red--text">
                                        <strong>* </strong>
                                      </span>
                                      {{ $t('bom_item_quantity') }}
                                    </template>
                                  </v-text-field>
                                </ValidationProvider>
                              </v-col>
                              <v-col cols="12" sm="6" md="4" xl="4" lg="4">
                                <ValidationProvider
                                  :name="$t('bom_item_price')"
                                  :rules="{ required: true, regex: /^[0-9]+$/ }"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    v-model="editedItem.Price"
                                    required
                                    :error-messages="errors[0]"
                                    clearable
                                    @keyup.enter="focusNextInput"
                                  >
                                    <template v-slot:label>
                                      <span class="red--text">
                                        <strong>* </strong>
                                      </span>
                                      {{ $t('bom_item_price') }}
                                    </template>
                                  </v-text-field>
                                </ValidationProvider>
                              </v-col>
                              <v-col cols="12" sm="6" md="4" xl="4" lg="4">
                                <ValidationProvider
                                  :name="$t('bom_item_warranty_period')"
                                  :rules="{ required: true, regex: /^[0-9]+$/ }"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    v-model="editedItem.WarrantyPeriod"
                                    required
                                    :error-messages="errors[0]"
                                    clearable
                                    @keyup.enter="focusNextInput"
                                  >
                                    <template v-slot:label>
                                      <span class="red--text">
                                        <strong>* </strong>
                                      </span>
                                      {{ $t('bom_item_warranty_period') }}
                                    </template>
                                  </v-text-field>
                                </ValidationProvider>
                              </v-col>
                            </v-row>
                          </ValidationObserver>
                        </v-container>
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="red darken-1" text @click="closeEditBomItem">
                          {{ $t('common_cancel') }}
                        </v-btn>
                        <v-btn color="green darken-1" text @click="addBomItem">
                          {{ $t('common_confirm') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  <delete-dialog
                    :isAllTextFieldDisable="isAllTextFieldDisable"
                    :isLoadingSaveBtn="isLoadingSaveBtn"
                    :isShowDeleteDialog="isShowDeleteBomItemDialog"
                    :deleteMessage="'bom_item_delete_dialog_message'"
                    @closeDelete="closeDeleteBomItem"
                    @confirmDelete="confirmDeleteBomItem"
                  />
                </v-toolbar>
              </template>
              <template v-slot:[`item.Edit`]="{ item }">
                <v-btn
                  icon
                  small
                  @click="isShowEditBomItemDialogBtn(item)"
                  :disabled="isAllTextFieldDisable"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="isShowDeleteBomItemDialogBtn(item)"
                  :disabled="isAllTextFieldDisable"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="red darken-1"
                text
                @click="closeEditBomDialog"
                :disabled="isAllTextFieldDisable"
              >
                {{ $t('common_cancel') }}
              </v-btn>
              <v-btn color="green darken-1" text @click="saveBom" :loading="isLoadingSaveBtn">
                {{ $t('common_save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="isBomItemNullDialog" max-width="500" persistent>
          <v-card color="error">
            <v-card-title class="text-h1"></v-card-title>
            <v-card-text class="white--text text-h5">
              <v-icon class="white--text text--lighten-1">mdi-alert-octagon</v-icon>
              {{ $t('bom_save_alert') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="white" text @click="isBomItemNullDialog = false">
                {{ $t('common_close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <delete-dialog
          :isAllTextFieldDisable="isDeleteBtnDisable"
          :isLoadingSaveBtn="isLoadingDeleteBtn"
          :isShowDeleteDialog="isShowDeleteBomDialog"
          :deleteMessage="'bom_delete_dialog_message'"
          @closeDelete="closeDeleteBom"
          @confirmDelete="confirmDeleteBom"
        />
      </v-toolbar>
    </template>
    <template v-slot:[`item.IsActive`]="{ item }">
      <v-icon v-if="item.IsActive" color="green">mdi-check</v-icon>
      <v-icon v-else color="red">mdi-close</v-icon>
    </template>
    <template v-slot:[`item.CreateTime`]="{ item }">
      {{ convertToDateTimeFormat(item.CreateTime) }}
    </template>
    <template v-slot:[`item.Edit`]="{ item }">
      <v-btn icon small @click="isShowEditBomDialogBtn(item)">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn icon small @click="isShowDeleteDialogBtn(item)">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length" style="background-color: rgb(223, 219, 219);">
        <v-simple-table style="background-color: rgb(223, 219, 219);">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('bom_item_number') }}
                </th>
                <th class="text-left">
                  {{ $t('bom_item_name') }}
                </th>
                <th class="text-right">
                  {{ $t('bom_item_quantity') }}
                </th>
                <th class="text-right">
                  {{ $t('bom_item_price') }}
                </th>
                <th class="text-right">
                  {{ $t('bom_item_warranty_period') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="simpleItem in item.BomItems" :key="simpleItem.Number">
                <td class="text-left">{{ simpleItem.Number }}</td>
                <td class="text-left">{{ simpleItem.Name }}</td>
                <td class="text-right">{{ simpleItem.Quantity }}</td>
                <td class="text-right">{{ simpleItem.Price }}</td>
                <td class="text-right">{{ simpleItem.WarrantyPeriod }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { extend } from 'vee-validate'
import { regex } from 'vee-validate/dist/rules'
import { HttpBom } from '@/js/http-Manager'
import ItemFilterAllTypeAutocomplete from '@/components/ItemFilterAllTypeAutocomplete.vue'
import DeleteDialog from '@/components/DeleteDialog.vue'
import axios from 'axios'

extend('regex', regex)

export default {
  components: { ItemFilterAllTypeAutocomplete, DeleteDialog },
  name: 'DefaultBom',
  data () {
    return {
      getIntersectingObj: null,
      isShowEditBomDialog: false,
      isShowDeleteBomDialog: false,

      searchBom: '',
      bomList: [],
      editedBomIndex: -1,
      editedBom: {
        IsActive: true,
        Number: '',
        Name: '',
        Price: 0,
        Unit: '組',
        Quantity: 0,
        Describe: '',
        BomItems: [],
        CreateTime: 0,
        CreateBy: '',
        UpdateTime: 0,
        UpdateBy: '',
        Remark: ''
      },
      defaultBom: {
        IsActive: true,
        Number: '',
        Name: '',
        Price: 0,
        Unit: '組',
        Describe: '',
        Quantity: 0,
        BomItems: [],
        CreateTime: 0,
        CreateBy: '',
        UpdateTime: 0,
        UpdateBy: '',
        Remark: ''
      },

      isShowEditBomItemDialog: false,
      isShowDeleteBomItemDialog: false,

      searchBomItem: '',
      editedBomItemIndex: -1,
      editedItem: {
        Number: '',
        Name: '',
        Quantity: 0,
        Price: 0,
        RepairPrice: 0,
        WarrantyPeriod: 0
      },
      defaultItem: {
        Number: '',
        Name: '',
        Quantity: 0,
        Price: 0,
        RepairPrice: 0,
        WarrantyPeriod: 0
      },
      isLoadingBom: false,
      isLoadingSaveBtn: false,
      isAllTextFieldDisable: false,
      isLoadingDeleteBtn: false,
      isDeleteBtnDisable: false,
      isBomItemNullDialog: false,
      bomSource: null,
      updateBomSource: null,
      addBomSource: null,
      deleteBomSource: null
    }
  },

  computed: {
    bomDialogTitle () {
      return this.editedBomIndex === -1 ? this.$t('bom_new_btn') : this.$t('bom_edit_btn')
    },
    bomItemDialogTitle () {
      return this.editedBomItemIndex === -1
        ? this.$t('bom_item_new_btn')
        : this.$t('bom_item_edit_btn')
    },
    bomDataTableHeaders () {
      return [
        { text: this.$t('common_is_active'), value: 'IsActive' },
        { text: this.$t('bom_name'), value: 'Name' },
        { text: this.$t('bom_describe'), value: 'Describe' },
        { text: this.$t('bom_price'), value: 'Price' },
        { text: this.$t('bom_create_time'), value: 'CreateTime' },
        { text: this.$t('bom_create_by'), value: 'CreateBy' },
        { text: this.$t('common_edit'), value: 'Edit', sortable: false },
        { text: '', value: 'data-table-expand', sortable: false }
      ]
    },
    bomItemDataTableHeaders () {
      return [
        { text: this.$t('bom_item_number'), value: 'Number' },
        { text: this.$t('bom_item_name'), value: 'Name' },
        { text: this.$t('bom_item_quantity'), value: 'Quantity' },
        { text: this.$t('bom_item_price'), value: 'Price' },
        { text: this.$t('bom_item_warranty_period'), value: 'WarrantyPeriod' },
        { text: this.$t('common_edit'), value: 'Edit', sortable: false }
      ]
    },
    headerProps () {
      return { sortByText: this.$t('installation_acceptance_header_sort') }
    }
  },
  async mounted () {
    await this.getAllBoms()
  },
  methods: {
    focusNextInput (event) {
      let form = document.getElementsByTagName('input')
      let targets = []
      for (let i = 0; i < form.length; i++) {
        if (
          form[i].type !== 'hidden' &&
          !form[i].disabled &&
          !form[i].readOnly &&
          form[i].type !== 'checkbox'
        ) {
          targets.push(form[i])
        }
      }
      let index = Array.prototype.indexOf.call(targets, event.target)
      if (index !== -1) {
        let next = index + 1
        if (next < targets.length) {
          targets[next].focus()
          targets[next].select()
        }
      }
    },
    setEditedItem (val) {
      this.editedItem = JSON.parse(JSON.stringify(val))
    },
    convertToDateTimeFormat (timestamp) {
      return this.$moment(new Date(timestamp)).format('YYYY-MM-DD')
    },
    isShowEditBomDialogBtn (item) {
      this.editedBomIndex = this.bomList.indexOf(item)
      let str = JSON.stringify(item)
      this.editedBom = JSON.parse(str)
      this.isShowEditBomDialog = true
    },

    isShowDeleteDialogBtn (item) {
      this.editedBomIndex = this.bomList.indexOf(item)
      let str = JSON.stringify(item)
      this.editedBom = JSON.parse(str)
      this.isShowDeleteBomDialog = true
    },

    async confirmDeleteBom () {
      this.isLoadingDeleteBtn = true
      this.isDeleteBtnDisable = true
      this.isLoadingBom = true
      if (this.deleteBomSource) {
        this.deleteBomSource.cancel('cancel')
      }
      let cancelToken = axios.CancelToken
      this.deleteBomSource = cancelToken.source()
      await HttpBom.DeleteBom(this.bomList[this.editedBomIndex].Id, this.deleteBomSource.token)
      this.deleteBomSource = null
      this.isLoadingDeleteBtn = false
      this.isDeleteBtnDisable = false
      this.isLoadingBom = false
      await this.getAllBoms()
      // this.bomList.splice(this.editedBomIndex, 1)
      this.closeDeleteBom()
    },

    async closeEditBomDialog () {
      this.isShowEditBomDialog = false
      this.isLoadingSaveBtn = false
      this.isAllTextFieldDisable = false
      this.$nextTick(() => {
        let str = JSON.stringify(this.defaultBom)
        this.editedBom = JSON.parse(str)
        this.editedBomIndex = -1
      })
      await this.getAllBoms()
    },

    closeDeleteBom () {
      this.isShowDeleteBomDialog = false
      this.$nextTick(() => {
        let str = JSON.stringify(this.defaultBom)
        this.editedBom = JSON.parse(str)
        this.editedBomIndex = -1
      })
    },
    async getAllBoms () {
      if (this.bomSource) {
        this.bomSource.cancel('cancel')
      }
      let cancelToken = axios.CancelToken
      this.bomSource = cancelToken.source()
      this.isLoadingBom = true

      let response = await HttpBom.GetAllBoms(this.bomSource.token)
      if (response) {
        this.bomList = response.status === 200 ? response.data.Data : []
        this.bomSource = null
      }
      this.isLoadingBom = false
    },

    async saveBom () {
      let isExist =
        this.editedBom.Id !== ''
          ? this.bomList.findIndex(
            (x) =>
              x.Id !== this.editedBom.Id &&
                x.Name === this.editedBom.Name &&
                x.Describe === this.editedBom.Describe
          ) !== -1
          : this.bomList.findIndex(
            (x) => x.Name === this.editedBom.Name && x.Describe === this.editedBom.Describe
          ) !== -1
      if (!isExist) {
        const isValid = await this.$refs.dialogNameAndPrice.validate()
        if (isValid) {
          if (this.editedBom.BomItems.length === 0) {
            this.isBomItemNullDialog = true
          } else {
            this.isLoadingSaveBtn = true
            this.isLoadingBom = true
            this.setAllTextFieldDisable()
            if (this.editedBomIndex > -1) {
              if (this.updateBomSource) {
                this.updateBomSource.cancel('cancel')
              }
              let cancelToken = axios.CancelToken
              this.updateBomSource = cancelToken.source()
              await HttpBom.UpdateBom(this.editedBom, this.updateBomSource.token)
              this.updateBomSource = null
            } else {
              if (this.addBomSource) {
                this.addBomSource.cancel('cancel')
              }
              let cancelToken = axios.CancelToken
              this.addBomSource = cancelToken.source()
              await HttpBom.AddBom(this.editedBom, this.addBomSource.token)
              this.addBomSource = null
            }
            await this.getAllBoms()
            this.closeEditBomDialog()
          }
        }
      } else {
        alert('此BOM已存在')
      }
    },
    setAllTextFieldDisable () {
      this.isAllTextFieldDisable = true
    },

    isShowEditBomItemDialogBtn (item) {
      this.editedBomItemIndex = this.editedBom.BomItems.indexOf(item)
      let str = JSON.stringify(item)
      this.editedItem = JSON.parse(str)
      this.isShowEditBomItemDialog = true
    },

    isShowDeleteBomItemDialogBtn (item) {
      this.editedBomItemIndex = this.editedBom.BomItems.indexOf(item)
      let str = JSON.stringify(item)
      this.editedItem = JSON.parse(str)
      this.isShowDeleteBomItemDialog = true
    },

    confirmDeleteBomItem () {
      this.editedBom.BomItems.splice(this.editedBomItemIndex, 1)
      this.closeDeleteBomItem()
    },

    closeEditBomItem () {
      this.isShowEditBomItemDialog = false
      this.$nextTick(() => {
        let str = JSON.stringify(this.defaultItem)
        this.editedItem = JSON.parse(str)
        this.editedBomItemIndex = -1
      })
    },

    closeDeleteBomItem () {
      this.isShowDeleteBomItemDialog = false
      this.$nextTick(() => {
        let str = JSON.stringify(this.defaultItem)
        this.editedItem = JSON.parse(str)
        this.editedBomItemIndex = -1
      })
    },
    async addBomItem () {
      const isValid = await this.$refs.dialogBomItem.validate()
      if (isValid) {
        if (this.editedBomItemIndex > -1) {
          this.editedBom.BomItems.splice(this.editedBomItemIndex, 1, this.editedItem)
        } else {
          this.editedBom.BomItems.push(this.editedItem)
        }
        this.closeEditBomItem()
      }
    },
    copyBom () {
      this.editedBom.Id = ''
      this.editedBomIndex = -1
    }
  }
}
</script>
